import { createSignal, createEffect, onCleanup, Show, type JSX, type ParentProps } from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import DisclaimerGate from '@components/privacy/DisclaimerGate';
import InstallPrompt from '@components/pwa/InstallPrompt';
import AgeGate from '@components/ui/AgeGate';
import { authStore } from '@stores/session';

const NAV = [
  { href: '/symptom-match', label: 'Manage Symptoms' },
  { href: '/strains', label: 'Strain Database' },
  { href: '/stock', label: 'Live Stock' },
  { href: '/watchlist', label: 'Watchlist' },
  { href: '/travel', label: 'Travel' },
  { href: '/blog', label: 'Blog' },
  { href: '/forum', label: 'Forum' },
] as const;

export default function AppShell(props: ParentProps): JSX.Element {
  const [menuOpen, setMenuOpen] = createSignal(false);
  const location = useLocation();

  // Close the menu whenever the route changes.
  createEffect(() => {
    void location.pathname;
    setMenuOpen(false);
  });

  createEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = menuOpen() ? 'hidden' : '';
  });
  onCleanup(() => {
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  });

  return (
    <div class="min-h-screen flex flex-col">
      <header class="px-6 py-4 flex items-center justify-between border-b border-brand-hex/60">
        <A href="/" class="font-display text-xl font-bold text-brand-green">
          Terp Track
        </A>
        <nav aria-label="Primary" class="hidden md:flex items-center gap-5 text-sm font-medium">
          {NAV.map((item) => (
            <A
              href={item.href}
              class="text-brand-green hover:text-brand-green-soft transition-colors"
              activeClass="text-brand-moss"
            >
              {item.label}
            </A>
          ))}
          {/* My Strains + My Diary live as a pill cluster — wrapping
              them in a tight flex group keeps gap-2 between the two
              pills without affecting the gap-5 spacing of surrounding
              nav links and icons. */}
          <div class="flex items-center gap-2">
            {authStore.user() && (
              <A
                href="/my-strains"
                class="rounded-full bg-brand-green text-brand-cream px-4 py-1.5"
                activeClass="bg-brand-moss"
              >
                My Strains
              </A>
            )}
            <A
              href={authStore.user() ? '/diary' : '/sign-in'}
              class="rounded-full bg-brand-green text-brand-cream px-4 py-1.5"
              activeClass="bg-brand-moss"
            >
              {authStore.user() ? 'My Diary' : 'Sign in'}
            </A>
          </div>
          {authStore.user() && (
            <A
              href="/messages"
              class="text-brand-green hover:text-brand-green-soft transition-colors"
              activeClass="text-brand-moss"
              aria-label="Messages"
              title="Messages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                <path d="M3.505 2.365A41.37 41.37 0 0 1 9 2c1.863 0 3.697.124 5.495.365 1.247.167 2.14 1.188 2.14 2.392v4.486c0 1.204-.893 2.225-2.14 2.392a41.446 41.446 0 0 1-2.456.24v2.34a.75.75 0 0 1-1.25.555l-2.668-2.37A41.417 41.417 0 0 1 3.505 11.9c-1.247-.167-2.14-1.188-2.14-2.392V4.757c0-1.204.893-2.225 2.14-2.392Z" />
              </svg>
            </A>
          )}
          {authStore.user() && (
            <A
              href="/settings"
              class="text-brand-green hover:text-brand-green-soft transition-colors"
              activeClass="text-brand-moss"
              aria-label="Account settings"
              title="Account settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                <path fill-rule="evenodd" d="M8.34 1.804A1 1 0 0 1 9.32 1h1.36a1 1 0 0 1 .98.804l.295 1.473c.497.144.968.34 1.409.581l1.25-.834a1 1 0 0 1 1.262.125l.962.962a1 1 0 0 1 .125 1.262l-.834 1.25c.241.441.437.912.581 1.409l1.473.294a1 1 0 0 1 .804.98v1.361a1 1 0 0 1-.804.98l-1.473.295a6.95 6.95 0 0 1-.581 1.409l.834 1.25a1 1 0 0 1-.125 1.262l-.962.962a1 1 0 0 1-1.262.125l-1.25-.834a6.953 6.953 0 0 1-1.409.581l-.294 1.473a1 1 0 0 1-.98.804H9.32a1 1 0 0 1-.98-.804l-.295-1.473a6.957 6.957 0 0 1-1.409-.581l-1.25.834a1 1 0 0 1-1.262-.125l-.962-.962a1 1 0 0 1-.125-1.262l.834-1.25a6.957 6.957 0 0 1-.581-1.409l-1.473-.294A1 1 0 0 1 1.01 10.68V9.32a1 1 0 0 1 .804-.98l1.473-.295c.144-.497.34-.968.581-1.409l-.834-1.25a1 1 0 0 1 .125-1.262l.962-.962A1 1 0 0 1 5.383 3.04l1.25.834a6.953 6.953 0 0 1 1.409-.581l.294-1.489ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd" />
              </svg>
            </A>
          )}
        </nav>
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen()}
          class="md:hidden p-2 -mr-2 text-brand-green"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-7 h-7">
            <path
              fill-rule="evenodd"
              d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </header>

      <Show when={menuOpen()}>
        <div
          class="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
        <nav
          aria-label="Mobile navigation"
          class="md:hidden fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] bg-brand-cream shadow-2xl p-6 flex flex-col gap-4 overflow-y-auto"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="font-display text-lg font-bold text-brand-green">Menu</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              class="p-2 -mr-2 text-brand-green"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-6 h-6">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
          {NAV.map((item) => (
            <A
              href={item.href}
              class="text-brand-green text-base font-medium py-2 border-b border-brand-hex/40"
              activeClass="text-brand-moss"
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </A>
          ))}
          {authStore.user() && (
            <A
              href="/my-strains"
              class="mt-2 rounded-full bg-brand-green text-brand-cream px-4 py-2 text-center font-medium"
              onClick={() => setMenuOpen(false)}
            >
              My Strains
            </A>
          )}
          <A
            href={authStore.user() ? '/diary' : '/sign-in'}
            class="rounded-full bg-brand-green text-brand-cream px-4 py-2 text-center font-medium"
            onClick={() => setMenuOpen(false)}
          >
            {authStore.user() ? 'My Diary' : 'Sign in'}
          </A>
          {authStore.user() && (
            <A
              href="/messages"
              class="text-brand-green text-base font-medium py-2 border-b border-brand-hex/40"
              activeClass="text-brand-moss"
              onClick={() => setMenuOpen(false)}
            >
              Messages
            </A>
          )}
          {authStore.user() && (
            <A
              href="/settings"
              class="text-brand-green text-base font-medium py-2 border-b border-brand-hex/40"
              activeClass="text-brand-moss"
              onClick={() => setMenuOpen(false)}
            >
              Account settings
            </A>
          )}
        </nav>
      </Show>

      <main class="flex-1">
        <DisclaimerGate>{props.children}</DisclaimerGate>
      </main>
      <footer class="px-6 py-6 text-xs text-brand-green/80 border-t border-brand-hex/60 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <p>
          Terp Track is an informational patient support tool for tracking personal medication
          efficacy and does not constitute medical advice.
        </p>
        <nav aria-label="Secondary" class="flex flex-wrap gap-4">
          <A href="/coa/upload" class="hover:underline">Upload a COA</A>
          <A href="/blog" class="hover:underline">Blog</A>
          <A href="/forum" class="hover:underline">Forum</A>
          <A href="/travel" class="hover:underline">Travel</A>
          {authStore.isAdmin() && (
            <A href="/admin" class="hover:underline">Admin</A>
          )}
        </nav>
      </footer>
      <InstallPrompt />
      <AgeGate />
    </div>
  );
}
